import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import perryPhoto from "@/assets/perry-knoppert.jpg";
import { foundingMembers } from "@/data/foundingMembers";

const VISIBLE_COUNT = 8;
const ROTATE_INTERVAL = 3500;

const roles: { role: string; body: string; to?: string; href?: string; cta?: string }[] = [
  { role: "Founder & storyteller", body: "Perry \"dr. Octopus\" Knoppert, who started this by accident and keeps telling the story.", href: "https://www.perryknoppert.com", cta: "Visit Perry's website" },
  {
    role: "Founding Members",
    body: "The people who stepped in first and keep carrying the movement forward.",
    to: "/founding-members",
    cta: "Meet the Founding Members",
  },
  { role: "Stewards", body: "The humans who look after the nonprofit, its promises and its money." },
  { role: "Hosts", body: "Members who hold the rooms: circles, hangouts, conversations, local gatherings." },
  { role: "Project leads", body: "Whoever picked up a question and decided to carry it further." },
  {
    role: "Scientists",
    body: "Researchers who bring rigour, method and peer review into the same room as lived experience.",
    to: "/science-member",
    cta: "Science Membership",
  },
  {
    role: "Wisdom Members",
    body: "People who bring lived experience, unlearning and long memory to the questions we ask.",
    to: "/wisdom-membership",
    cta: "Wisdom Membership",
  },
];

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

const HoldersSection = () => {
  const allFaces = useMemo(
    () => [
      { name: "Perry Knoppert", photo: perryPhoto },
      ...foundingMembers.map((m) => ({ name: m.name, photo: m.photo })),
    ],
    []
  );

  const [visibleFaces, setVisibleFaces] = useState(() => shuffle(allFaces).slice(0, VISIBLE_COUNT));
  const [fading, setFading] = useState(false);
  const timeoutRef = useRef<number | null>(null);

  const rotateFaces = useCallback(() => {
    setFading(true);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = window.setTimeout(() => {
      setVisibleFaces(shuffle(allFaces).slice(0, VISIBLE_COUNT));
      setFading(false);
      timeoutRef.current = null;
    }, 400);
  }, [allFaces]);

  useEffect(() => {
    const timer = setInterval(rotateFaces, ROTATE_INTERVAL);
    return () => {
      clearInterval(timer);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [rotateFaces]);

  return (
    <section id="who-holds-the-space" className="py-24 md:py-32 bg-muted/40">
      <div className="container mx-auto px-4 max-w-5xl">
        <div className="mb-12">
          <p className="text-sm uppercase tracking-[0.2em] text-coral-text mb-4">Who holds the space</p>
          <h2 className="text-3xl md:text-5xl font-heading font-bold mb-6">
            Humans, not{" "}
            <span className="text-gradient-coral">organizational boxes.</span>
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl">
            No pyramid describes an organism. But trust needs legibility, so here is who founded this, who stewards it,
            who holds the rooms and who leads the projects.
          </p>
        </div>

        <Link
          to="/founding-members"
          aria-label="Meet the Founding Members"
          className="block mb-4"
        >
          <div className={`flex flex-wrap gap-3 transition-opacity duration-300 ${fading ? "opacity-40" : "opacity-100"}`}>
            {visibleFaces.map((f) => (
              <img
                key={f.name}
                src={f.photo}
                alt={f.name}
                loading="lazy"
                width={96}
                height={96}
                className="w-16 h-16 md:w-20 md:h-20 rounded-full object-cover border border-border/60 transform-gpu transition-transform duration-300 hover:scale-105"
                style={{ objectPosition: "center 25%" }}
              />
            ))}
          </div>
        </Link>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-12">
          <p className="text-sm text-muted-foreground">
            A few faces from the mycelium. Everyone who holds the space is human.
          </p>
          <button
            type="button"
            onClick={rotateFaces}
            aria-label="Shuffle faces"
            className="inline-flex items-center gap-1.5 min-h-[44px] text-sm font-medium text-coral-text hover:text-coral transition-colors"
          >
            Shuffle →
          </button>
        </div>

        <dl className="grid sm:grid-cols-2 gap-x-10 gap-y-6 mb-12">
          {roles.map((r) => (
            <div key={r.role}>
              <dt className="font-heading font-bold text-foreground mb-1">{r.role}</dt>
              <dd className="text-muted-foreground leading-relaxed">{r.body}</dd>
              {(r.to || r.href) && (
                <dd className="mt-1">
                  {r.to ? (
                    <Link
                      to={r.to}
                      className="inline-flex items-center gap-1 min-h-[44px] text-sm font-medium text-coral-text hover:text-coral transition-colors"
                    >
                      {r.cta} →
                    </Link>
                  ) : (
                    <a
                      href={r.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 min-h-[44px] text-sm font-medium text-coral-text hover:text-coral transition-colors"
                    >
                      {r.cta} ↗
                    </a>
                  )}
                </dd>
              )}
            </div>
          ))}
        </dl>

        <div className="flex flex-wrap items-center gap-x-8 gap-y-2">
          <Link
            to="/who-holds-the-space"
            className="inline-flex items-center gap-2 min-h-[44px] text-coral-text hover:text-coral font-medium transition-colors"
          >
            See who holds the space →
          </Link>
        </div>
      </div>
    </section>
  );
};

export default HoldersSection;
